import { KatDropdownButton, KatTooltip } from "@amzn/katal-react";
import classnames from "classnames";
import React, { type FC } from "react";
import { type WithTranslation, withTranslation } from "react-i18next";
import { BulkOperationsActions } from "src/api/interfaces/bulk_operations";
import type { ConsolidatedDocument } from "src/api/interfaces/document_listing";
import { InboundStatus } from "src/api/interfaces/inbound_document";
import { Operation } from "src/api/interfaces/permissions";
import { checkPermissionOnCountry } from "src/components/AICLayout/AICHeader/Permissions/PermissionCheckers";
import { useCountrySelection } from "src/components/Contexts/CountrySelectionContext";
import { useDocumentList } from "src/components/Contexts/DocumentList/Provider";
import type { DocumentRow } from "src/components/Contexts/DocumentList/types";
import { usePermissions } from "src/components/Contexts/PermissionsContext";
import type { DataTableSelectionProps } from "src/components/DataTable/DataTableDefinition";
import BulkOperationsConfirmationModal from "src/components/Pages/DocumentList/BulkOperations/BulkOperationsConfirmationModal";
import { DEFAULT_USER_ACTION_WINDOW } from "src/components/UserActions/ValidationUserActionsDropdown";
import styles from "src/styles.module.scss";

const ACTIONS_TOOLTIP_LABEL = "aic_doc_page_user_action_dropdown_tooltip";
const ACTIONS_DROPDOWN_LABEL = "aic_doc_page_user_action_dropdown_actions";
const MAX_DOCUMENT_IDS_ON_TOOLTIP = 10;
const NOT_SELECTED = "NOT_SELECTED";
const USE_CASE_SEPARATOR = ",";

export const actionTranslation: Record<BulkOperationsActions, string> = {
  [BulkOperationsActions.Revalidate]:
    "aic_internal_bulk_operations_dropdown_action_revalidate",
  [BulkOperationsActions.ForceAccept]:
    "aic_internal_bulk_operations_dropdown_action_force_accept",
  [BulkOperationsActions.ForceReject]:
    "aic_internal_bulk_operations_dropdown_action_force_reject",
};

const actionPermissionMapping: Record<BulkOperationsActions, Operation> = {
  [BulkOperationsActions.Revalidate]: Operation.PerformComplianceUserActions,
  [BulkOperationsActions.ForceAccept]: Operation.PerformCriticalActions,
  [BulkOperationsActions.ForceReject]: Operation.PerformCriticalActions,
};

const documentOnActionTimeWindow = (
  document: DocumentRow & DataTableSelectionProps
): boolean => {
  const creationDate = new Date(Date.parse(document.creationDate));
  creationDate.setMonth(
    creationDate.getMonth() + Number.parseInt(DEFAULT_USER_ACTION_WINDOW)
  );
  return creationDate.getTime() > Date.now();
};

export const getTemporaryRejectedTooltipLabel = (
  documents: DocumentRow[],
  tempRejectedDocumentsMessage: string
): string => {
  const tempRejectedDocumentIds = documents.map(
    (document) =>
      document.documentDetails!.officialDocumentId ?? document.documentId
  );

  const toolTipMessage = [
    tempRejectedDocumentsMessage,
    tempRejectedDocumentIds.slice(0, MAX_DOCUMENT_IDS_ON_TOOLTIP).join(", "),
  ].join(" ");

  if (tempRejectedDocumentIds.length > MAX_DOCUMENT_IDS_ON_TOOLTIP) {
    return `${toolTipMessage}, ...`;
  } else {
    return toolTipMessage;
  }
};

export const checkDocumentAllowUserAction = (
  document: DocumentRow & DataTableSelectionProps
): boolean => {
  return (
    (document.status === InboundStatus.PENDING_ACTIONS ||
      document.status === InboundStatus.TEMPORARILY_REJECTED) &&
    documentOnActionTimeWindow(document)
  );
};

export const bulkOperationRowSelectionEvaluator = (
  doc: DocumentRow & DataTableSelectionProps,
  selectionList: Array<ConsolidatedDocument & DataTableSelectionProps>
): boolean => {
  const selected = selectionList.filter((item) => {
    return item.selected;
  });

  return (
    (!selected ||
      selected.every((item) => {
        return (
          doc.documentType === item.documentType &&
          doc.countryCode === item.countryCode
        );
      })) &&
    checkDocumentAllowUserAction(doc)
  );
};

export const getBulkOperationsAllowedActions = (
  permissions: Map<string, Map<string, Operation[]>>,
  countryCode?: string
): BulkOperationsActions[] => {
  return [
    BulkOperationsActions.Revalidate,
    BulkOperationsActions.ForceAccept,
    BulkOperationsActions.ForceReject,
  ].filter((key) => {
    return checkPermissionOnCountry(
      permissions,
      countryCode,
      actionPermissionMapping[key]
    );
  });
};

const BulkOperations: FC<WithTranslation> = ({ t }) => {
  const { permissions } = usePermissions();
  const { selectedCountry } = useCountrySelection();
  const hasBOPermission = checkPermissionOnCountry(
    permissions,
    selectedCountry?.countryCode,
    Operation.PerformBulkOperations
  );
  const {
    state: { documents },
  } = useDocumentList();

  const [handlerModalAction, setHandlerModalAction] =
    React.useState<string>("");

  const action = (event: KatDropdownButton.ActionEvent) => {
    setHandlerModalAction(event.detail.action);
  };

  const selectedDocuments = Object.values(documents).filter(
    (document: DocumentRow) => document.selected
  );
  const useCasesSet = new Set();
  selectedDocuments.forEach((document: DocumentRow) =>
    useCasesSet.add(document.useCase)
  );
  const useCases = Array.from(useCasesSet).join(USE_CASE_SEPARATOR);

  const documentIds = selectedDocuments.map(
    (document: DocumentRow) => document.documentId
  );
  const countryCode = selectedCountry && selectedCountry.countryCode;
  const { documentType } = selectedDocuments.length
    ? selectedDocuments[0]
    : { documentType: NOT_SELECTED };

  const allowedActions = getBulkOperationsAllowedActions(
    permissions,
    selectedCountry?.countryCode
  );

  const temporarilyRejectedSelected = selectedDocuments.filter(
    (document: DocumentRow) =>
      document.status === InboundStatus.TEMPORARILY_REJECTED
  );

  return hasBOPermission && !!allowedActions.length ? (
    <div>
      <BulkOperationsConfirmationModal
        documentList={documentIds}
        documentType={documentType}
        useCases={useCases}
        countryCode={countryCode || NOT_SELECTED}
        action={handlerModalAction}
        setHandlerModalAction={setHandlerModalAction}
      />
      <KatDropdownButton
        id="bulk-operations-user-actions-dropdown"
        variant="primary"
        size="base"
        disabled={!selectedDocuments.length}
        options={[
          { label: t(ACTIONS_DROPDOWN_LABEL) },
          ...allowedActions.map((key) => {
            return {
              disabled:
                key === BulkOperationsActions.ForceAccept &&
                temporarilyRejectedSelected.length > 0,
              label: t(actionTranslation[key]),
              action: key,
            };
          }),
        ]}
        onAction={action}
      />
      {temporarilyRejectedSelected.length > 0 && (
        <KatTooltip
          id="bulk-operations-user-actions-tooltip"
          label={getTemporaryRejectedTooltipLabel(
            temporarilyRejectedSelected,
            t(ACTIONS_TOOLTIP_LABEL)
          )}
          trigger-icon="info-circle"
          position="right"
          size="tiny"
          className={classnames(styles.padding_8)}
        />
      )}
    </div>
  ) : (
    <div />
  );
};

export default withTranslation()(BulkOperations);
